/**
 * BranchForm — used in both create and edit modals for Branch resource.
 * Uses react-hook-form + zodResolver. Schema from shared/schemas.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

const branchFormSchema = z.object({
  code: z.string().min(1, 'Bắt buộc').max(50),
  name: z.string().min(1, 'Bắt buộc').max(200),
  address: z.string().min(1, 'Bắt buộc'),
  phone: z.string().max(20).optional().or(z.literal('')),
  email: z.string().email('Email không hợp lệ').optional().or(z.literal('')),
  status: z.enum(['active', 'inactive', 'closed']),
  openingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().or(z.literal('')),
});

export type BranchFormValues = z.infer<typeof branchFormSchema>;

interface BranchFormProps {
  defaultValues?: Partial<BranchFormValues>;
  isEdit?: boolean;
  isSubmitting?: boolean;
  onSubmit: (values: BranchFormValues) => void;
  onCancel: () => void;
}

export function BranchForm({
  defaultValues,
  isEdit,
  isSubmitting,
  onSubmit,
  onCancel,
}: BranchFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<BranchFormValues>({
    resolver: zodResolver(branchFormSchema),
    defaultValues: {
      status: 'active',
      ...defaultValues,
    },
  });

  useEffect(() => {
    reset({ status: 'active', ...defaultValues });
  }, [defaultValues, reset]);

  const status = watch('status');

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      {/* Code — read-only when editing */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="code" className="text-sm font-medium">
          Mã cơ sở <span className="text-destructive">*</span>
        </Label>
        <Input
          id="code"
          className="h-10 font-mono"
          placeholder="VD: CLC-Q1"
          disabled={isEdit}
          {...register('code')}
        />
        {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
      </div>

      {/* Name */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="name" className="text-sm font-medium">
          Tên cơ sở <span className="text-destructive">*</span>
        </Label>
        <Input id="name" className="h-10" placeholder="CS Quận 1" {...register('name')} />
        {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
      </div>

      {/* Address */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="address" className="text-sm font-medium">
          Địa chỉ <span className="text-destructive">*</span>
        </Label>
        <Textarea id="address" rows={2} placeholder="Địa chỉ đầy đủ" {...register('address')} />
        {errors.address && <p className="text-xs text-destructive">{errors.address.message}</p>}
      </div>

      {/* Phone + Email — 2 col on md+ */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="phone" className="text-sm font-medium">Số điện thoại</Label>
          <Input id="phone" type="tel" className="h-10" {...register('phone')} />
          {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email" className="text-sm font-medium">Email</Label>
          <Input id="email" type="email" className="h-10" {...register('email')} />
          {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
        </div>
      </div>

      {/* Status + Opening date — 2 col on md+ */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Trạng thái <span className="text-destructive">*</span>
          </Label>
          <Select value={status} onValueChange={(v) => setValue('status', v as BranchFormValues['status'])}>
            <SelectTrigger className="h-10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Hoạt động</SelectItem>
              <SelectItem value="inactive">Tạm dừng</SelectItem>
              <SelectItem value="closed">Đã đóng</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="openingDate" className="text-sm font-medium">Ngày khai trương</Label>
          <Input id="openingDate" type="date" className="h-10" {...register('openingDate')} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isEdit ? 'Đang lưu…' : 'Đang tạo…'}
            </span>
          ) : isEdit ? (
            'Lưu thay đổi'
          ) : (
            'Tạo cơ sở'
          )}
        </Button>
      </div>
    </form>
  );
}
